/**
 * Phase 36 — a country's gateway experiments: the read, and the four
 * writes — start one on a container, stop it early, apply its winner,
 * judge now. Every write refreshes the Drop Generator queries (the day
 * preview changes with the pool).
 * For Media Devoted | Created By Ciro Autiero
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DropGenExperimentAction, DropGenExperimentCreate, DropGenExperimentRow } from '@trafficpulse/shared';

import { api } from '@/services/api';

interface Envelope<T> {
  data: T;
}

const BASE = '/drop-generator/experiments';

/** Under the Drop Generator root key, so every mutation's invalidate refreshes it too. */
export const experimentKeys = (countryKey: string) => ['drop-generator', 'experiments', countryKey] as const;

export function useExperiments(countryKey: string, enabled = true) {
  return useQuery({
    queryKey: experimentKeys(countryKey),
    queryFn: async () => {
      const res = await api.get<Envelope<DropGenExperimentRow[]>>(`${BASE}/${countryKey}`);
      return res.data.data;
    },
    enabled,
    staleTime: 10_000,
  });
}

export function useExperimentMutations(countryKey: string) {
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['drop-generator'] });
  };
  const start = useMutation({
    mutationFn: async (input: { containerId: string; body: DropGenExperimentCreate }) => {
      const res = await api.post<Envelope<DropGenExperimentRow>>(`${BASE}/${countryKey}/containers/${input.containerId}/start`, input.body);
      return res.data.data;
    },
    onSuccess: invalidate,
  });
  const stop = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<Envelope<DropGenExperimentRow>>(`${BASE}/${countryKey}/${id}/stop`);
      return res.data.data;
    },
    onSuccess: invalidate,
  });
  const apply = useMutation({
    mutationFn: async (input: { id: string; action: DropGenExperimentAction }) => {
      const res = await api.post<Envelope<DropGenExperimentRow>>(`${BASE}/${countryKey}/${input.id}/apply`, { action: input.action });
      return res.data.data;
    },
    onSuccess: invalidate,
  });
  const judge = useMutation({
    mutationFn: async () => {
      const res = await api.post<Envelope<{ judged: number; done: number }>>(`${BASE}/${countryKey}/judge`);
      return res.data.data;
    },
    onSuccess: invalidate,
  });
  return { start, stop, apply, judge };
}
